import { useEffect } from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/landing/Nav";
import Footer from "@/components/landing/Footer";
import FinalCTA from "@/components/landing/FinalCTA";
import FAQ from "@/components/shared/FAQ";
import MedCard from "@/components/shared/MedCard";
import BeforeAfterGallery, { TRANSFORMATIONS } from "@/components/shared/BeforeAfterGallery";
import { Flame, Heart, Sparkles, Zap, Leaf } from "lucide-react";
import SEO from "@/components/shared/SEO";

const WOMENS_IMG =
  "https://customer-assets.emergentagent.com/job_med-wall-results/artifacts/q8ql05y9_image.png";

const categories = [
  { icon: Flame, label: "Weight Loss", desc: "GLP-1 protocols built for your body." },
  { icon: Heart, label: "Hormone Balance", desc: "Restore the hormones running the show." },
  { icon: Sparkles, label: "Hair & Skin", desc: "Tretinoin, minoxidil, compounded formulas." },
  { icon: Zap, label: "Energy & Mood", desc: "End the afternoon crash. Wake up clear." },
  { icon: Leaf, label: "Wellness & Longevity", desc: "Live longer. Feel better doing it." },
];

const programs = [
  {
    name: "The Reset",
    sub: "GLP-1 Weight Loss",
    body: "Semaglutide or tirzepatide, monthly clinician care, MIC+B12 support, nutrition guidance.",
    meds: ["Semaglutide", "MIC+B12"],
  },
  {
    name: "The Balance",
    sub: "Hormone Therapy",
    body: "Bloodwork-driven HRT. Clinician-titrated. Quarterly labs and adjustments included.",
    meds: ["Estradiol", "Progesterone"],
  },
  {
    name: "The Glow",
    sub: "Skin & Hair",
    body: "Compounded tretinoin, tranexamic acid, oral and topical minoxidil — protocols tuned to your skin.",
    meds: ["Tretinoin", "Minoxidil"],
  },
];

const womensFaq = [
  { q: "Is Women's Shop for me?", a: "If you're a woman who wants medical guidance on weight loss, hormones, skin, hair, energy, or longevity — yes. We work with women 18 and older nationwide." },
  { q: "Do I need a referral?", a: "No. Take the assessment, a licensed clinician reviews your file, orders labs if needed, and writes your plan." },
  { q: "How fast will I see results?", a: "GLP-1 patients typically see noticeable change within 4–8 weeks. Hormone optimization is felt within 2–4 weeks." },
  { q: "Is this covered by insurance?", a: "We work directly with compounding pharmacies — no insurance required. Flat monthly pricing, HSA / FSA accepted." },
  { q: "Can I pause my program?", a: "Yes. Pause, adjust, or change protocols anytime via your dashboard or your care team." },
];

export default function WomensShop() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <main className="bg-[var(--leen-bg)] text-white overflow-x-hidden min-h-screen">
      <SEO
        title="The Women's Shop · GLP-1, Hormones, Hair & Skin"
        path="/womens-shop"
        description="Provider-guided programs for women: GLP-1 weight loss, hormone balance, hair and skin protocols, energy and mood. Licensed clinicians, transparent pricing."
      />
      <Nav />

      {/* HERO */}
      <section className="relative pt-16 md:pt-20 min-h-[88vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={WOMENS_IMG} alt="The Women's Shop" className="img-zoom h-full w-full object-cover object-[60%_top]" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(236,44,126,0.55) 0%, rgba(236,44,126,0.15) 35%, rgba(10,14,26,0.55) 100%)" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,14,26,0.15) 35%, rgba(10,14,26,0.75) 75%, rgba(10,14,26,0.98) 100%)" }} />
        </div>
        <div className="relative px-5 md:px-8 lg:px-12 max-w-[1500px] mx-auto pb-16 md:pb-24 w-full">
          <p className="eyebrow text-[var(--leen-pink)] mb-5 fade-up">For Her · Welcome In</p>
          <p className="font-script text-[var(--leen-pink)] text-[64px] sm:text-[88px] md:text-[110px] lg:text-[140px] leading-none -mb-4 md:-mb-6 lg:-mb-8 italic fade-up fade-up-1">
            The
          </p>
          <h1 className="font-display text-[68px] sm:text-[100px] md:text-[140px] lg:text-[200px] leading-[0.86] tracking-tight text-white fade-up fade-up-1">
            WOMEN'S<br />
            <span className="text-[var(--leen-pink)]">SHOP</span>
          </h1>
          <p className="mt-7 font-body text-[18px] md:text-[22px] text-[var(--leen-ink-soft)] max-w-2xl fade-up fade-up-2">
            Medically guided programs in weight loss, hormones, skin, hair, energy, and longevity —
            curated for women, prescribed by licensed clinicians.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 fade-up fade-up-3">
            <a href="#start-assessment" className="btn-pink">Start Your Assessment <span aria-hidden>→</span></a>
            <a href="#categories" className="btn-ghost">Browse Categories</a>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section id="categories" className="bg-[var(--leen-bg)] py-20 md:py-28 border-t border-[var(--leen-line)]">
        <div className="px-5 md:px-8 lg:px-12 max-w-[1500px] mx-auto">
          <div className="text-center mb-14">
            <p className="eyebrow text-[var(--leen-pink)] mb-3">Five paths · One you</p>
            <h2 className="font-display text-[40px] sm:text-[60px] md:text-[80px] leading-none text-white">
              <span className="text-[var(--leen-pink)]">CHOOSE</span> YOUR PATH
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
            {categories.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.label} data-testid={`womens-cat-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} className="group bg-[var(--leen-card)] border border-[var(--leen-line)] rounded-xl p-6 md:p-7 hover:border-[var(--leen-pink)] hover:-translate-y-1 transition-all">
                  <span className="w-11 h-11 rounded-md bg-[var(--leen-pink)]/15 border border-[var(--leen-pink)]/40 flex items-center justify-center text-[var(--leen-pink)] mb-5">
                    <Icon size={18} />
                  </span>
                  <h3 className="font-display text-[18px] md:text-[22px] text-white tracking-wide leading-tight mb-2">
                    {c.label}
                  </h3>
                  <p className="text-[13px] text-[var(--leen-ink-soft)] leading-relaxed">{c.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED PROGRAMS */}
      <section className="bg-[var(--leen-card)] py-20 md:py-28 border-y border-[var(--leen-line)]">
        <div className="px-5 md:px-8 lg:px-12 max-w-[1500px] mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <p className="font-script text-[var(--leen-pink)] text-[44px] md:text-[60px] leading-none italic -mb-2">
                Featured
              </p>
              <h2 className="font-display text-[36px] sm:text-[52px] md:text-[64px] leading-none text-white">
                Designed.<br />Prescribed.<br /><span className="text-[var(--leen-pink)]">Yours.</span>
              </h2>
            </div>
            <p className="font-body italic text-[15px] text-[var(--leen-ink-soft)] max-w-md">
              Three flagship programs — bundles of medication, clinician care, and ongoing support.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {programs.map((p, i) => (
              <div key={p.name} data-testid={`womens-program-${i}`} className="bg-[var(--leen-bg)] border border-[var(--leen-line)] rounded-xl p-7 hover:border-[var(--leen-pink)] transition-colors">
                <p className="eyebrow text-[var(--leen-pink)] mb-4">{p.sub}</p>
                <h3 className="font-display text-[34px] md:text-[40px] text-white leading-none mb-5">{p.name}</h3>
                <p className="text-[14px] text-[var(--leen-ink-soft)] leading-relaxed mb-6">{p.body}</p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {p.meds.map((med) => (
                    <span key={med} className="eyebrow px-3 py-1.5 rounded-full border border-[var(--leen-pink)]/40 text-[var(--leen-pink)]">
                      {med}
                    </span>
                  ))}
                </div>
                <a href="#start-assessment" className="eyebrow text-[var(--leen-pink)] inline-flex items-center gap-1.5">
                  Start This Program <span aria-hidden>→</span>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MEDICATIONS */}
      <section className="bg-[var(--leen-bg)] py-20 md:py-28">
        <div className="px-5 md:px-8 lg:px-12 max-w-[1500px] mx-auto">
          <div className="text-center mb-14">
            <p className="font-script text-[var(--leen-pink)] text-[44px] md:text-[60px] leading-none italic -mb-2">
              Made for
            </p>
            <h2 className="font-display text-[36px] sm:text-[52px] md:text-[64px] leading-none text-white">
              MEDICATIONS<br /><span className="text-[var(--leen-pink)]">FOR HER</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
            <MedCard slug="semaglutide" />
            <MedCard slug="tirzepatide" />
            <MedCard slug="oral-glp1" />
            <MedCard slug="mic-b12" />
            <MedCard slug="l-carnitine" />
          </div>
        </div>
      </section>

      {/* RESULTS */}
      <section className="bg-[var(--leen-card)] py-20 md:py-28 border-y border-[var(--leen-line)]">
        <div className="px-5 md:px-8 lg:px-12 max-w-[1500px] mx-auto">
          <div className="text-center mb-12">
            <p className="font-script text-[var(--leen-pink)] text-[44px] md:text-[60px] leading-none italic -mb-2">
              She did it
            </p>
            <h2 className="font-display text-[36px] sm:text-[52px] md:text-[64px] leading-none text-white mb-3">
              <span className="text-[var(--leen-pink)]">SHE</span> SHOWED UP
            </h2>
            <p className="text-[15px] italic text-[var(--leen-ink-soft)] max-w-xl mx-auto">
              Sustainable change. Confidence restored. Real members. Results vary.
            </p>
          </div>
          <BeforeAfterGallery items={TRANSFORMATIONS.slice(0, 4)} />
          <div className="mt-10 flex justify-center">
            <Link to="/results" className="btn-ghost">See All Transformations <span aria-hidden>→</span></Link>
          </div>
        </div>
      </section>

      <FAQ items={womensFaq} accent="pink" title="WOMEN'S SHOP · FAQ" />
      <FinalCTA />
      <Footer />
    </main>
  );
}
